import React, { useContext, useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import CancelAndNextFooter from "../../../../../../common/CancelAndNextFooter";
import { Alert, Link, Toggle, Typeahead } from "@merge-api/merge-javascript-shared";
import FieldMappingsGridContainer from "../../components/FieldMappingsGridContainer";
import FieldMappingModalTitle from "../components/FieldMappingModalTitle";
import FieldMappingModalBackButton from "../components/FieldMappingModalBackButton";
import FieldMappingModalContext from "../context/FieldMappingModalContext";
import LinkedAccountFieldMappingsContext from "../../context/LinkedAccountFieldMappingsContext";
import RemoteFieldCard from "../components/RemoteFieldCard";
import {
  CommonModelFieldDefinition,
  FieldMappingMetaResponse,
  FieldMappingModalStep,
} from "../../../../../../../models/Entities";
import CommonModelFieldDropdown from "../../../../../../shared/CommonModelFieldDropdown";
import {
  createCommonModelOverride,
  doesModelHaveRemoteDataEnabled,
  getFieldMappingModalTitle,
  getOverrideOptions,
} from "../helpers";
import { CONFIGURATION_COMMON_MODELS_PATH } from "../../../../../../../router/RouterUtils";
import FieldMappingTargetBadge from "../../../../../../shared/FieldMappingTargetBadge";
import FieldMappingModalHeader from "../components/FieldMappingModalHeader";
import useAppContext from "../../../../../../context/useAppContext";

const CommonModelOverrideMappingCreation = () => {
  const {
    linkedAccount,
    onModalClose,
    selectedRemoteField,
    jsonAdvancedMappingExpression,
    linkedAccountMappingInfo,
    selectedFieldMappingPathway,
    commonModelsToMappingConfig,
  } = useContext(FieldMappingModalContext);
  const { setFieldMappingTargetAndInstances } = useContext(LinkedAccountFieldMappingsContext);
  const { setCustomMappingMetaResponse } = useContext(FieldMappingModalContext);

  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user.is_jmes_path_deprecation_dashboard_enabled ?? false;
  const disableOrgScopeEditingFlag = user.is_org_scope_editing_disabled;

  const [isCreating, setIsCreating] = useState(false);
  const [isOrganizationWide, setIsOrganizationWide] = useState(false);
  const [fieldToOverride, setFieldToOverride] = useState<{
    name: string;
    type: string;
    description: string;
    format: string | null;
  } | null>(null);

  const commonModelSelected = selectedRemoteField?.commonModelName;
  const availableFieldsAndOverrides = (linkedAccountMappingInfo as FieldMappingMetaResponse)
    .available_model_and_field_overrides;

  return (
    <>
      <FieldMappingModalTitle
        title={getFieldMappingModalTitle(selectedFieldMappingPathway!)}
        onClose={onModalClose}
      />
      <div className="px-6 mt-5">
        <FieldMappingModalBackButton
          destinationStep={FieldMappingModalStep.REMOTE_FIELD_SELECTION}
        />
        <div className="text-base mt-3.5">
          <p className="mb-2">
            Remote Fields can only be mapped to their respective Common Model. This change will be
            reflected after the next sync which will be a full resync of data for{" "}
            {linkedAccount?.end_user.organization_name}.
          </p>
        </div>
        <div className="flex flex-col mt-6 gap-y-4">
          <FieldMappingModalHeader
            integrationName={linkedAccount?.integration.name!}
            integrationSquareImage={linkedAccount?.integration.square_image}
            isCommonModelOverride
            commonModel={commonModelSelected}
          />

          <FieldMappingsGridContainer>
            <>
              <div className="flex-row flex-shrink-0">
                <RemoteFieldCard
                  displayName={selectedRemoteField?.display_name || ""}
                  apiEndpointPath={selectedRemoteField?.api_endpoint_path}
                  type={selectedRemoteField?.type}
                  jsonAdvancedMappingExpression={jsonAdvancedMappingExpression}
                  badge={
                    <FieldMappingTargetBadge
                      type={
                        isOrganizationWide
                          ? "org-wide-target-field"
                          : "linked-account-specific-target-field"
                      }
                    />
                  }
                />
                {!disableOrgScopeEditingFlag && (
                  <Toggle
                    checked={isOrganizationWide}
                    onChange={() => setIsOrganizationWide(!isOrganizationWide)}
                    label="Convert to integration-wide mapping"
                    className="mt-3"
                  />
                )}
              </div>
              <div className="flex p-4 pt-3 flex-shrink-0">
                <ArrowRight size={20} />
              </div>
              {!doesModelHaveRemoteDataEnabled(
                selectedRemoteField!.commonModelName,
                commonModelsToMappingConfig,
              ) ? (
                <Alert color="yellow" icon={<AlertTriangle size={16} />}>
                  <span>
                    <Link
                      target="_blank"
                      href={`${CONFIGURATION_COMMON_MODELS_PATH}/${linkedAccount!.category}#${
                        selectedRemoteField!.commonModelName
                      }`}
                    >
                      Enable Remote Data
                    </Link>
                    {"  "} to create a new Common Model field for this model
                  </span>
                </Alert>
              ) : (
                <>
                  <Typeahead
                    placeholder={`Select ${commonModelSelected} fields...`}
                    options={getOverrideOptions(commonModelSelected, availableFieldsAndOverrides!)}
                    getOptionLabel={(option) => {
                      return option.field_name;
                    }}
                    renderOption={(option) => {
                      return (
                        <CommonModelFieldDropdown
                          fieldDescription={option.field_description}
                          fieldType={option.field_format || option.field_type}
                          fieldName={option.field_name}
                        />
                      );
                    }}
                    onChange={(_: any, selectedOption) => {
                      if (selectedOption) {
                        setFieldToOverride({
                          name: selectedOption.field_name as string,
                          type: selectedOption.field_type,
                          description: selectedOption.field_description,
                          format: selectedOption.field_format,
                        });
                      } else {
                        setFieldToOverride(null);
                      }
                    }}
                    filterOptions={(options: any, { inputValue }: any) => {
                      return options.filter((option: CommonModelFieldDefinition) => {
                        const inputValueToLowerCase = inputValue.toLowerCase();
                        return (
                          option.field_name.includes(inputValueToLowerCase) ||
                          option.field_description.includes(inputValueToLowerCase)
                        );
                      });
                    }}
                  />
                </>
              )}
            </>
          </FieldMappingsGridContainer>
        </div>
      </div>
      <div className="pr-6 !mt-6">
        <CancelAndNextFooter
          buttonText="Save mapping"
          onCancel={onModalClose}
          onNext={() => {
            createCommonModelOverride(
              selectedRemoteField,
              linkedAccount,
              commonModelSelected,
              jsonAdvancedMappingExpression,
              fieldToOverride,
              setIsCreating,
              setFieldMappingTargetAndInstances,
              onModalClose,
              setCustomMappingMetaResponse,
              isOrganizationWide,
              isJmesPathDeprecationEnabled,
            );
          }}
          isNextDisabled={fieldToOverride == null || fieldToOverride == undefined}
          isLoading={isCreating}
        />
      </div>
    </>
  );
};
export default CommonModelOverrideMappingCreation;
